import type { DerivedProfileSummary } from '@clarity/contract';

type Payouts = DerivedProfileSummary['payouts'];
type Row = Payouts['rows'][number];

/** A paid-at date range, both bounds `YYYY-MM-DD` day strings or absent. */
export interface PaidAtRange {
  from: string | null;
  to: string | null;
}

/** True when both bounds are empty (the filter is a no-op). */
export function isEmptyRange(r: PaidAtRange): boolean {
  return !r.from && !r.to;
}

/**
 * Filter payouts by paid-at date, WITHOUT recomputing any balance. Returns a new
 * payouts object `{ ...payouts, rows, statementChecks }`; every other field and
 * every surviving month's cashFlow/earned is carried through byte-identical —
 * the filter only ever shrinks the two arrays.
 *
 * - empty range  → the input unchanged (identity).
 * - from > to    → zero rows (surfaced as a distinct message by the view).
 * - a row is retained iff its `paidAt` is non-null AND its `YYYY-MM-DD` day is
 *   within `[from, to]` inclusive (open on an absent bound). A null `paidAt` is
 *   excluded whenever ANY bound is set.
 * - a `statementChecks` month is retained iff ≥1 of its `payoutIds` survived.
 *
 * Lexicographic `YYYY-MM-DD` comparison is correct for ISO day strings and
 * avoids timezone drift.
 */
export function filterPaidAt(payouts: Payouts, range: PaidAtRange): Payouts {
  if (isEmptyRange(range)) return payouts;

  const inRange = (row: Row): boolean => {
    if (row.paidAt === null) return false;
    const day = row.paidAt.slice(0, 10); // YYYY-MM-DD (UTC day)
    if (range.from && day < range.from) return false;
    if (range.to && day > range.to) return false;
    return true;
  };

  const rows = payouts.rows.filter(inRange);
  const kept = new Set(rows.map((r) => r.externalPayoutId));
  const statementChecks = payouts.statementChecks.filter((c) =>
    c.payouts.payoutIds.some((id) => kept.has(id)),
  );
  // Careem (clarity-careem-payout-status): a cycle-month is retained iff ≥1 of
  // its cycles survived the filter — same rule as a statement month.
  const cycleChecks = payouts.cycleChecks.filter((c) =>
    c.cycles.payoutIds.some((id) => kept.has(id)),
  );

  return { ...payouts, rows, statementChecks, cycleChecks };
}
